import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SiteHeader, SiteFooter, BIDCOURT_URL, ADMIN_PHONE, LEAGUES_CONTACT, Check } from "../components/Layout";
import "./leagues.css";

/* ---------------------------------------------------------------------------
   Copy
   --------------------------------------------------------------------------- */

const STEPS = [
  { n: "01", t: "Set rules and teams", b: "Purse, squad size, women and men minimums, DUPR tiers or skill levels, and bid steps. The pickleball template is ready to go." },
  { n: "02", t: "Players sign up", b: "Share one link on WhatsApp. Players add their DUPR rating or pick their level, and pay the league fee by UPI; you approve them." },
  { n: "03", t: "Bid live", b: "Owners bid from their phones while the big screen shows every sale. Undo anything with one tap." },
  { n: "04", t: "Squads, sorted", b: "Every team ends with a legal squad: the rules make sure nobody runs out of purse before their places are filled." },
];

const FEATS = [
  { k: "A", t: "DUPR tiers or skill levels", b: "Place players by DUPR rating, or use simple levels like Beginner, Intermediate and Advanced. Each gets its own base price." },
  { k: "W", t: "Women and men minimums", b: "Every team can field women's, men's and mixed doubles pairs." },
  { k: "2", t: "Caps on top players", b: "Limit how many top-tier or Advanced players any one team can buy." },
  { k: "QR", t: "Team links and QR codes", b: "Owners join from WhatsApp or a QR code. No app to download." },
  { k: "↺", t: "Undo anything", b: "Undo the last bid, reverse a sale and refund the purse, or pause at any time." },
  { k: "TV", t: "Big screen and stream overlay", b: "Show bids on a TV, or add a transparent overlay to your livestream." },
];

const PLANS = [
  { name: "Free", price: "₹0", per: "", who: "Try it with friends", items: ["1 auction", "2 teams of up to 10 players each", "Big screen and team links"] },
  { name: "Event pass", price: "₹7,500", per: "per auction", who: "One league or corporate event", items: ["Up to 8 teams and 120 players", "Registration with UPI fees", "Results export"], pick: true },
  { name: "Custom", price: "Let's talk", per: "", who: "When the Event pass doesn't fit", items: ["Auctions every month or every season", "More teams or players than the Event pass", "Your own format, rules or branding"], contact: true },
];

// WhatsApp goes to the admin number (Ace's); calls go to LEAGUES_CONTACT.
// The chat opens with the first message already typed.
const CUSTOM_WHATSAPP = `https://wa.me/${ADMIN_PHONE.href.replace(/\D/g, "")}?text=${encodeURIComponent(
  "Hi! I'd like to talk about a custom plan for Vibe & Volley Leagues."
)}`;

const FAQ = [
  { q: "Do team owners need to download an app?", a: "No. Each owner gets a private link that opens in any phone browser." },
  { q: "What if our players don't have DUPR ratings?", a: "Switch the league to skill levels. Players pick Beginner, Intermediate or Advanced (or levels you name yourself) when they register, and you can move anyone by hand. With DUPR tiers, unrated players start in the lowest tier." },
  { q: "How are league fees collected?", a: "Players pay you directly by UPI and enter the reference number. You tick them off as paid." },
  { q: "Can we fix a mistake during the auction?", a: "Yes. Undo the last bid, reverse a sale and refund the purse, or pause the auction at any time." },
];

/* ---------------------------------------------------------------------------
   Demo auction data (sample names, shown as a demo)
   --------------------------------------------------------------------------- */

const ME = "you";
const TEAMS = [
  { id: ME, name: "Your team", color: "var(--vv-accent)", purse: 50000, need: 3 },
  { id: "kk", name: "Kitchen Kings", color: "#7CC4FF", purse: 62000, need: 4 },
  { id: "dd", name: "Dink Dynasty", color: "#FFB86B", purse: 48000, need: 3 },
  { id: "ts", name: "Third Shot Club", color: "#FF8A80", purse: 55000, need: 4 },
];
const PLAYERS = [
  { name: "Kabir Sethi", ini: "KS", tier: "A", dupr: "4.62", side: "Right side", base: 8000 },
  { name: "Ananya Rao", ini: "AR", tier: "B", dupr: "4.10", side: "Left side", base: 4000 },
  { name: "Meher Joshi", ini: "MJ", tier: "A", dupr: "4.55", side: "Either side", base: 8000 },
  { name: "Vikram Kulkarni", ini: "VK", tier: "C", dupr: "3.40", side: "Right side", base: 1500 },
];
const SALES = [
  ["Aarav Mehta", "Kitchen Kings", "#7CC4FF", 14000],
  ["Ishita Patil", "Dink Dynasty", "#FFB86B", 9500],
  ["Rohan Deshmukh", "Third Shot Club", "#FF8A80", 22000],
  ["Sara Khan", "Kitchen Kings", "#7CC4FF", 6500],
  ["Neel Gupta", "Dink Dynasty", "#FFB86B", 12000],
  ["Tara Iyer", "Third Shot Club", "#FF8A80", 4500],
];
const LOWEST = 1500;
const LOT_SECS = 10;
const RING = 213.6; // circumference of the timer ring (r = 34)

const fmt = (n) => Number(n).toLocaleString("en-IN");
const inc = (n) => (n < 10000 ? 500 : n < 25000 ? 1000 : 2500);
const maxBid = (t) => t.purse - Math.max(0, t.need - 1) * LOWEST;

/* ---------------------------------------------------------------------------
   Small hooks
   --------------------------------------------------------------------------- */

function useMedia(query) {
  const [match] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches);
  return match;
}

// Floodlights come on the first time the page opens in a browser session.
function useIntro(reduce) {
  const [show, setShow] = useState(false);
  const [played, setPlayed] = useState(false);
  const started = useRef(false);
  useLayoutEffect(() => {
    if (reduce) return undefined;
    if (!started.current) {
      let seen = true;
      try {
        seen = window.sessionStorage.getItem("vv-leagues-intro") === "1";
        if (!seen) window.sessionStorage.setItem("vv-leagues-intro", "1");
      } catch (e) {
        seen = true; // storage blocked: skip rather than replay on every visit
      }
      if (seen) return undefined;
      started.current = true;
      setShow(true);
      setPlayed(true);
    }
    const t = setTimeout(() => setShow(false), 1650);
    return () => clearTimeout(t);
  }, [reduce]);
  return { show, played };
}

/* ---------------------------------------------------------------------------
   Demo auction engine: plain JS, so React only renders its state.
   --------------------------------------------------------------------------- */

function createLot({ onChange, onSold }) {
  const teams = TEAMS.map((t) => ({ ...t }));
  const s = { teams, L: null, lotNo: 7, idx: 0 };
  let tick = null;
  let bot = null;
  let next = null;
  let dead = false;

  const nextAmt = () => (s.L.bid == null ? s.L.p.base : s.L.bid + inc(s.L.bid));

  function place(id) {
    const L = s.L;
    if (!L || L.done) return;
    const t = teams.find((x) => x.id === id);
    const amt = nextAmt();
    if (L.leader === id || amt > maxBid(t)) return;
    L.bid = amt;
    L.leader = id;
    L.left = LOT_SECS;
    schedule();
    onChange();
  }

  function schedule() {
    clearTimeout(bot);
    if (dead || !s.L || s.L.done) return;
    const L = s.L;
    const heat = Math.max(0.15, 1 - (L.bid ?? L.p.base) / (L.p.base * 3.2));
    bot = setTimeout(() => {
      const bots = teams.filter((t) => t.id !== ME && t.id !== L.leader && nextAmt() <= maxBid(t));
      if (bots.length && Math.random() < heat) place(bots[Math.floor(Math.random() * bots.length)].id);
      else schedule();
    }, 900 + Math.random() * 1800);
  }

  function finish() {
    const L = s.L;
    L.done = true;
    clearTimeout(bot);
    const lead = teams.find((t) => t.id === L.leader);
    if (lead) {
      lead.purse -= L.bid;
      lead.need = Math.max(1, lead.need - 1);
      onSold(lead, L);
    }
    onChange();
    next = setTimeout(() => {
      s.idx += 1;
      s.lotNo += 1;
      if (teams[0].purse < 6000) teams.forEach((t, i) => { t.purse = TEAMS[i].purse; t.need = TEAMS[i].need; });
      start();
    }, 3800);
  }

  function start() {
    if (dead) return;
    clearInterval(tick);
    s.L = { p: PLAYERS[s.idx % PLAYERS.length], bid: null, leader: null, left: LOT_SECS, done: false };
    schedule();
    onChange();
    tick = setInterval(() => {
      const L = s.L;
      if (L.done) return;
      L.left = Math.max(0, L.left - 0.1);
      if (L.left <= 0) finish();
      else onChange();
    }, 100);
  }

  s.L = { p: PLAYERS[0], bid: null, leader: null, left: LOT_SECS, done: false, idle: true };
  return {
    state: s,
    start,
    bid: () => place(ME),
    nextAmt,
    destroy() {
      dead = true;
      clearInterval(tick);
      clearTimeout(bot);
      clearTimeout(next);
    },
  };
}

function createConfetti(canvas) {
  let bits = [];
  let raf = null;
  const ctx = canvas.getContext("2d");
  function loop() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    bits = bits.filter((b) => b.life-- > 0 && b.y < canvas.height + 40);
    bits.forEach((b) => {
      b.vy += 0.7; b.vx *= 0.985; b.x += b.vx; b.y += b.vy; b.r += b.vr;
      ctx.save(); ctx.translate(b.x, b.y); ctx.rotate(b.r); ctx.fillStyle = b.c; ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h); ctx.restore();
    });
    raf = bits.length ? requestAnimationFrame(loop) : null;
    if (!raf) ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
  return {
    burst(origin, colors, n) {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      const ox = origin.x * dpr;
      const oy = origin.y * dpr;
      for (let i = 0; i < n; i++) {
        bits.push({ x: ox, y: oy, vx: (Math.random() - 0.5) * 26, vy: -Math.random() * 22 - 6, w: 6 + Math.random() * 8, h: 4 + Math.random() * 6, r: Math.random() * 6, vr: (Math.random() - 0.5) * 0.4, c: colors[i % colors.length], life: 120 + Math.random() * 40 });
      }
      if (!raf) loop();
    },
    destroy() { cancelAnimationFrame(raf); raf = null; bits = []; },
  };
}

/* ---------------------------------------------------------------------------
   Pieces
   --------------------------------------------------------------------------- */

function Magnetic({ as: Tag = "a", fine, className = "", children, ...rest }) {
  const ref = useRef(null);
  const handlers = fine
    ? {
        onPointerMove: (e) => {
          const el = ref.current;
          const r = el.getBoundingClientRect();
          el.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.18}px, ${(e.clientY - r.top - r.height / 2) * 0.28}px)`;
        },
        onPointerLeave: () => { ref.current.style.transform = ""; },
      }
    : {};
  return <Tag ref={ref} className={`${className} lg-magnet`} {...handlers} {...rest}>{children}</Tag>;
}

function Intro() {
  return (
    <div className="lg-intro" aria-hidden="true">
      <div className="lg-intro__rig" /><div className="lg-intro__rig" /><div className="lg-intro__rig" /><div className="lg-intro__rig" />
      <svg viewBox="0 0 800 400">
        <rect x="20" y="20" width="760" height="360" />
        <line x1="400" y1="20" x2="400" y2="380" /><line x1="250" y1="20" x2="250" y2="380" /><line x1="550" y1="20" x2="550" y2="380" />
        <line x1="20" y1="200" x2="250" y2="200" /><line x1="550" y1="200" x2="780" y2="200" />
      </svg>
      <div className="lg-intro__word vv-display vv-display--italic">Lights on</div>
    </div>
  );
}

function HeroLot({ reduce, fine, delay, onSale }) {
  const lotRef = useRef(null);
  const canvasRef = useRef(null);
  const engine = useRef(null);
  const [, setTick] = useState(0);
  const [sold, setSold] = useState(null); // { color, key } for the flood
  const delayRef = useRef(delay);
  delayRef.current = delay;

  useEffect(() => {
    const lotEl = lotRef.current;
    const fx = createConfetti(canvasRef.current);
    const e = createLot({
      onChange: () => setTick((n) => n + 1),
      onSold: (team, L) => {
        const accent = getComputedStyle(lotEl).getPropertyValue("--vv-accent").trim() || "#18C566";
        const color = team.id === ME ? accent : team.color;
        setSold({ color: team.color, key: `${L.p.name}-${Date.now()}` });
        onSale([L.p.name, team.id === ME ? "Your team" : team.name, team.color, L.bid]);
        if (!reduce) {
          const r = lotEl.getBoundingClientRect();
          fx.burst({ x: r.left + r.width / 2, y: r.top + r.height * 0.45 }, [color, "#FFFFFF", accent, color], team.id === ME ? 160 : 70);
          lotEl.classList.remove("is-shake");
          void lotEl.offsetWidth;
          lotEl.classList.add("is-shake");
        }
        if (team.id === ME) {
          try { navigator.vibrate?.([60, 40, 120]); } catch (err) { /* not supported */ }
        }
      },
    });
    engine.current = e;
    setTick((n) => n + 1);
    let startTimer = null;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        startTimer = setTimeout(() => e.start(), reduce ? 0 : delayRef.current);
        io.disconnect();
      }
    });
    io.observe(lotEl);
    return () => {
      io.disconnect();
      clearTimeout(startTimer);
      e.destroy();
      fx.destroy();
    };
  }, [reduce, onSale]);

  const e = engine.current;
  const s = e ? e.state : { teams: TEAMS, L: { p: PLAYERS[0], bid: null, leader: null, left: LOT_SECS, done: false, idle: true }, lotNo: 7 };
  const { L, teams } = s;
  const me = teams[0];
  const lead = teams.find((t) => t.id === L.leader);
  const amt = L.bid == null ? L.p.base : L.bid + inc(L.bid);
  const hot = !L.idle && L.left <= 3;
  let going = "";
  if (lead && !L.done && L.left <= 3) going = L.left > 1.5 ? "Going once…" : "Going twice…";

  let btnText = `Raise paddle · Bid ${fmt(amt)}`;
  let disabled = false;
  if (L.idle) { btnText = "Starting the lot…"; disabled = true; }
  else if (L.done) { btnText = "Next lot in a moment"; disabled = true; }
  else if (L.leader === ME) { btnText = "You're the highest bidder"; disabled = true; }
  else if (amt > maxBid(me)) { btnText = `Max bid ${fmt(maxBid(me))} reached`; disabled = true; }

  const tilt = fine && !reduce
    ? {
        onPointerMove: (ev) => {
          const el = lotRef.current;
          const r = el.getBoundingClientRect();
          const x = (ev.clientX - r.left) / r.width - 0.5;
          const y = (ev.clientY - r.top) / r.height - 0.5;
          el.style.transform = `perspective(1000px) rotateY(${x * 6}deg) rotateX(${-y * 6}deg)`;
        },
        onPointerLeave: () => { lotRef.current.style.transform = ""; },
      }
    : {};

  return (
    <section className="lg-lot" ref={lotRef} aria-label="Demo auction: bid against three sample teams" {...tilt}>
      {L.done && lead && sold && <div key={sold.key} className="lg-flood" style={{ background: `radial-gradient(circle at 50% 60%, ${sold.color}, transparent 70%)` }} />}
      <div className="lg-lot__top"><span>Lot {String(s.lotNo).padStart(2, "0")} · Demo auction</span><span className="lg-live"><i />Live</span></div>
      <div className="lg-player">
        <div className="lg-avatar vv-display" aria-hidden="true">{L.p.ini}</div>
        <div>
          <p className="lg-pname vv-display">{L.p.name}</p>
          <div className="lg-chips">
            <span className={`lg-chip${L.p.tier === "A" ? " lg-chip--a" : ""}`}>Tier {L.p.tier}</span>
            <span className="lg-chip">DUPR {L.p.dupr}</span>
            <span className="lg-chip">{L.p.side}</span>
          </div>
        </div>
      </div>
      <div className="lg-bidrow">
        <div>
          <div className="lg-k">{lead ? "Current bid" : "Opening bid · base price"}</div>
          <div className="lg-bidnum" aria-live="polite"><span key={L.bid ?? "base"}>{fmt(L.bid ?? L.p.base)}</span></div>
          <div className="lg-leader">
            {lead && <><span className="lg-dot" style={{ background: lead.color }} />{lead.id === ME ? "You lead" : `${lead.name} leads`}</>}
          </div>
        </div>
        <div className={`lg-ring${hot ? " is-hot" : ""}`} aria-hidden="true">
          <svg viewBox="0 0 80 80"><circle className="lg-ring__bg" cx="40" cy="40" r="34" /><circle className="lg-ring__fg" cx="40" cy="40" r="34" strokeDasharray={RING} strokeDashoffset={RING * (1 - L.left / LOT_SECS)} /></svg>
          <span>{Math.ceil(L.left)}</span>
        </div>
      </div>
      <div className="lg-going" aria-live="polite">{going}</div>
      <button type="button" className="vv-btn vv-btn--accent lg-bidbtn" disabled={disabled} onClick={() => e && e.bid()}>{btnText}</button>
      <div className="lg-purse"><span>Your purse <b>{fmt(me.purse)}</b></span><span>Max bid <b>{fmt(maxBid(me))}</b></span></div>
      <div className="lg-teams">
        {teams.map((t) => (
          <div key={t.id} className={`lg-team${L.leader === t.id ? " is-leading" : ""}`} style={L.leader === t.id ? { borderColor: t.color } : undefined}>
            <b><span className="lg-dot" style={{ background: t.color }} />{t.name}</b>{fmt(t.purse)}
          </div>
        ))}
      </div>
      <div className={`lg-stamp${L.done ? " is-on" : ""}`} aria-hidden={!L.done}>
        {L.done && (
          <div className="lg-stamp__in vv-display vv-display--italic" style={{ color: lead ? lead.color : "var(--vv-on-strong-dim)" }}>
            {lead ? "Sold" : "Unsold"}
            <small>{lead ? `${lead.id === ME ? "To you" : `To ${lead.name}`} · ${fmt(L.bid)}` : "Back to the pool"}</small>
          </div>
        )}
      </div>
      <canvas className="lg-confetti" ref={canvasRef} aria-hidden="true" />
    </section>
  );
}

function Ticker({ sales }) {
  const items = sales.map(([n, t, c, a], i) => (
    <span className="lg-ticker__item" key={`${n}-${i}`}>Sold <b>{n}</b> <span className="lg-dot" style={{ background: c }} /> {t} <em>{fmt(a)}</em></span>
  ));
  return (
    <div className="lg-ticker" role="region" aria-label="Recent sales in the demo auction (sample data)">
      <span className="lg-ticker__tag">Demo sales</span>
      <div className="lg-ticker__view">
        <div className="lg-ticker__track">{items}<span aria-hidden="true" style={{ display: "contents" }}>{items}</span></div>
      </div>
    </div>
  );
}

function PhoneScreen({ step, on, bid }) {
  const cls = `lg-scr${on ? " is-on" : ""}`;
  if (step === 0) {
    return (
      <div className={cls}>
        <div className="lg-k">League rules</div>
        <div className="lg-scr__title">Sunday Smash League</div>
        <div className="lg-field"><span>Teams</span><b>6</b></div>
        <div className="lg-field"><span>Purse each</span><b>1,00,000</b></div>
        <div className="lg-field"><span>Squad</span><b>6 to 8 players</b></div>
        <div className="lg-field"><span>Women · Men minimum</span><b>2 · 2</b></div>
        <div className="lg-field"><span>Tier A cap</span><b>2 per team</b></div>
        <div className="lg-fakebtn">Save rules</div>
      </div>
    );
  }
  if (step === 1) {
    return (
      <div className={cls}>
        <div className="lg-k">Registration</div>
        <div className="lg-scr__title">Join the league</div>
        <div className="lg-pcard"><div className="lg-k">Tier B · DUPR 4.10</div><div className="lg-pcard__name">Ananya Rao</div><div className="lg-pcard__meta">Left side · Woman</div></div>
        <div className="lg-field"><span>League fee</span><b>₹1,500 by UPI</b></div>
        <div className="lg-field"><span>UPI reference</span><b>4120 9981 2207</b></div>
        <span className="lg-okbadge">Approved by organizer</span>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className={cls}>
        <div className="lg-k">Kitchen Kings · Live</div>
        <div className="lg-scr__title">Now bidding</div>
        <div className="lg-pcard"><div className="lg-pcard__name">Kabir Sethi</div><div className="lg-pcard__meta">Tier A · DUPR 4.62</div></div>
        <div className="lg-k">Current bid · Dink Dynasty</div>
        <div className="lg-big">{fmt(bid)}</div>
        <div className="lg-fakebtn">Bid {fmt(bid + inc(bid))}</div>
      </div>
    );
  }
  return (
    <div className={cls}>
      <div className="lg-k">Kitchen Kings</div>
      <div className="lg-scr__title">Squad complete</div>
      <div className="lg-sq">{["KS", "AR", "MJ", "PD", "SN", "VK"].map((x) => <i key={x} className="is-f">{x}</i>)}<i>+</i><i>+</i></div>
      <div className="lg-field"><span>Women · Men</span><b>2 · 4</b></div>
      <div className="lg-field"><span>Purse left</span><b>6,500</b></div>
      <span className="lg-okbadge">Legal squad</span>
    </div>
  );
}

function Story() {
  const [active, setActive] = useState(0);
  const [bid, setBid] = useState(14000);
  const list = useRef(null);

  useEffect(() => {
    const steps = list.current.querySelectorAll("[data-step]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => { if (en.isIntersecting) setActive(Number(en.target.dataset.step)); }),
      { rootMargin: "-45% 0px -45% 0px" }
    );
    steps.forEach((s) => io.observe(s));
    const t = setInterval(() => setBid((b) => (b >= 24000 ? 14000 : b + 1000)), 1600);
    return () => { io.disconnect(); clearInterval(t); };
  }, []);

  return (
    <div className="lg-story">
      <ol className="lg-steps" ref={list}>
        {STEPS.map((s, i) => (
          <li key={s.n} className={`lg-step${active === i ? " is-on" : ""}`} data-step={i}>
            <div className="lg-step__n vv-display">{s.n}</div>
            <h3>{s.t}</h3>
            <p>{s.b}</p>
            <div className="lg-step__mini" aria-hidden="true"><div className="lg-phone"><div className="lg-screen"><PhoneScreen step={i} on bid={bid} /></div></div></div>
          </li>
        ))}
      </ol>
      <div className="lg-phonewrap" aria-hidden="true">
        <div className="lg-phone"><div className="lg-screen">{STEPS.map((s, i) => <PhoneScreen key={s.n} step={i} on={active === i} bid={bid} />)}</div></div>
      </div>
    </div>
  );
}

function Guard() {
  const [purse, setPurse] = useState(40000);
  const [need, setNeed] = useState(4);
  const [base, setBase] = useState(1500);
  const held = Math.max(0, need - 1) * base;
  const max = Math.max(0, purse - held);
  return (
    <div className="lg-guard lg-reveal">
      <div className="lg-ctrl">
        <div><label htmlFor="lg-gp">Purse left <output htmlFor="lg-gp">{fmt(purse)}</output></label><input type="range" id="lg-gp" min="5000" max="100000" step="500" value={purse} onChange={(e) => setPurse(Number(e.target.value))} /></div>
        <div><label htmlFor="lg-gn">Players still needed <output htmlFor="lg-gn">{need}</output></label><input type="range" id="lg-gn" min="1" max="8" step="1" value={need} onChange={(e) => setNeed(Number(e.target.value))} /></div>
        <div><label htmlFor="lg-gb">Lowest base price <output htmlFor="lg-gb">{fmt(base)}</output></label><input type="range" id="lg-gb" min="500" max="4000" step="500" value={base} onChange={(e) => setBase(Number(e.target.value))} /></div>
      </div>
      <div>
        <div className="lg-k">Highest bid allowed right now</div>
        <div className="lg-maxbid vv-display" aria-live="polite">{fmt(max)}</div>
        <div className="lg-meter" aria-hidden="true">
          <div className="lg-meter__free" style={{ width: `${(max / purse) * 100}%` }} />
          <div className="lg-meter__held" style={{ width: `${(Math.min(held, purse) / purse) * 100}%` }} />
        </div>
        <div className="lg-legend"><span>Can bid</span><span>{need > 1 ? `Held back for ${need - 1} more place${need - 1 === 1 ? "" : "s"}: ${fmt(held)}` : "Last place: the whole purse is available"}</span></div>
      </div>
    </div>
  );
}

function BigScreen() {
  const tv = useRef(null);
  const [play, setPlay] = useState(0);
  const [overlay, setOverlay] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setPlay((p) => p || 1); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(tv.current);
    return () => io.disconnect();
  }, []);
  return (
    <div className="vv-wrap lg-tvrow">
      <div ref={tv} className={`lg-tv${play ? " is-play" : ""}${overlay ? " is-overlay" : ""}`} role="img" aria-label="Big screen preview: Kabir Sethi sold to Kitchen Kings for 18,500 (sample)">
        <div className="lg-tv__court" />
        <div className="lg-tv__full" key={`f${play}`}><div><div className="lg-tv__stamp vv-display vv-display--italic">Sold</div><div className="lg-tv__name vv-display">Kabir Sethi</div></div></div>
        <div className="lg-lower" key={`l${play}`} style={{ borderLeftColor: "#7CC4FF" }}><b>Kitchen Kings</b><span>Tier A · DUPR 4.62</span><span className="lg-lower__amt">18,500</span></div>
      </div>
      <div>
        <p className="lg-eyebrow">The big screen</p>
        <h2 id="tv-h" className="vv-display vv-display--italic lg-h2">Auction night, broadcast-ready</h2>
        <p className="lg-sub">Put it on the TV at the venue and every sale lands with a stamp. Streaming the night? Switch to the overlay and drop it over your camera feed.</p>
        <label className="lg-switch"><input type="checkbox" checked={overlay} onChange={(e) => setOverlay(e.target.checked)} /> Stream overlay mode</label>
        <div style={{ marginTop: 22 }}><button type="button" className="vv-btn vv-btn--ghost" onClick={() => setPlay((p) => p + 1)}>Replay the sale</button></div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Page
   --------------------------------------------------------------------------- */

export default function Leagues() {
  const reduce = useMedia("(prefers-reduced-motion: reduce)");
  const finePointer = useMedia("(pointer: fine)");
  const fine = finePointer && !reduce;
  const intro = useIntro(reduce);
  const [sales, setSales] = useState(SALES);
  const [js, setJs] = useState(false);
  const root = useRef(null);

  const onSale = React.useCallback((sale) => setSales((list) => [sale, ...list].slice(0, 8)), []);

  // Reveal sections as they scroll in; anything already on screen shows at once.
  useEffect(() => {
    setJs(true);
    const els = root.current.querySelectorAll(".lg-reveal");
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); }
    }), { rootMargin: "0px 0px -8% 0px" });
    els.forEach((el, i) => {
      el.style.transitionDelay = `${(i % 3) * 80}ms`;
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add("is-in");
      else io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const spotlight = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const featLight = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--fx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--fy", `${e.clientY - r.top}px`);
  };

  return (
    <div className="vv-page">
      <SiteHeader />
      <main ref={root} className={`lg${js ? " is-js" : ""}${intro.played ? " lg--intro" : ""}`} data-vv-surface="dark">
        {intro.show && <Intro />}

        <section aria-labelledby="hero-h" className="lg-hero" onPointerMove={fine ? spotlight : undefined}>
          <div className="lg-courtbg" aria-hidden="true">
            <svg viewBox="0 0 1400 700" preserveAspectRatio="xMidYMid slice">
              <rect x="80" y="60" width="1240" height="580" />
              <line x1="700" y1="60" x2="700" y2="640" /><line x1="480" y1="60" x2="480" y2="640" /><line x1="920" y1="60" x2="920" y2="640" />
              <line x1="80" y1="350" x2="480" y2="350" /><line x1="920" y1="350" x2="1320" y2="350" />
            </svg>
          </div>
          <div className="vv-wrap lg-hero__grid">
            <div>
              <p className="lg-eyebrow">Leagues &amp; auctions by Vibe &amp; Volley</p>
              <h1 id="hero-h" className="vv-display vv-display--italic lg-headline">
                <span className="lg-headline__line"><span>Your league.</span></span>
                <span className="lg-headline__line"><span>Your auction.</span></span>
              </h1>
              <p className="lg-lead">
                Run an IPL-style player auction for your pickleball league. Players sign up with their DUPR rating or skill level,
                owners bid live from their phones, and every sale lands on the big screen.
              </p>
              <div className="vv-btnrow">
                <Magnetic fine={fine} href={BIDCOURT_URL} className="vv-btn vv-btn--accent vv-btn--lg">Set up your league</Magnetic>
                <Magnetic fine={fine} href={BIDCOURT_URL} className="vv-btn vv-btn--ghost vv-btn--lg">Try a demo auction</Magnetic>
              </div>
              <p className="lg-note">Free to try with sample players. Nothing to install. Or bid on the demo lot right here.</p>
            </div>
            <HeroLot reduce={reduce} fine={fine} delay={intro.show ? 1800 : 600} onSale={onSale} />
          </div>
        </section>

        <Ticker sales={sales} />

        <section aria-labelledby="how-h" className="lg-band">
          <div className="vv-wrap">
            <p className="lg-eyebrow lg-reveal">How it works</p>
            <h2 id="how-h" className="vv-display vv-display--italic lg-h2 lg-reveal">From sign-ups to sold in one evening</h2>
            <Story />
          </div>
        </section>

        <section aria-labelledby="feat-h" className="lg-band lg-band--alt">
          <div className="vv-wrap">
            <p className="lg-eyebrow lg-reveal">Built for pickleball</p>
            <h2 id="feat-h" className="vv-display vv-display--italic lg-h2 lg-reveal">The rules referee the room</h2>
            <p className="lg-sub lg-reveal">Owners can't overspend and strand their own squad. Try the max-bid guard: every place a team still has to fill keeps the lowest base price in reserve.</p>
            <Guard />
            <div className="lg-feats">
              {FEATS.map((f) => (
                <div key={f.t} className="lg-feat lg-reveal" onPointerMove={fine ? featLight : undefined}>
                  <span className="lg-feat__k vv-display" aria-hidden="true">{f.k}</span>
                  <h3>{f.t}</h3>
                  <p>{f.b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="tv-h" className="lg-band">
          <BigScreen />
        </section>

        <section aria-labelledby="price-h" className="lg-band lg-band--alt">
          <div className="vv-wrap">
            <p className="lg-eyebrow lg-reveal">Pricing</p>
            <h2 id="price-h" className="vv-display vv-display--italic lg-h2 lg-reveal">Free to try, then pay per auction</h2>
            <div className="lg-paddles">
              {PLANS.map((p) => (
                <div key={p.name} className={`lg-paddle lg-reveal${p.pick ? " is-pick" : ""}`}>
                  <div className="lg-paddle__face">
                    {p.pick && <span className="lg-badge">Most leagues</span>}
                    <h3>{p.name}</h3>
                    <p className="lg-price">{p.price}{p.per && <small> {p.per}</small>}</p>
                    <p className="lg-paddle__who">{p.who}</p>
                    <ul>{p.items.map((it) => <li key={it}><Check /><span>{it}</span></li>)}</ul>
                    <div className="lg-paddle__cta">
                      {p.contact ? (
                        <>
                          <a href={CUSTOM_WHATSAPP} target="_blank" rel="noopener noreferrer" className="vv-btn vv-btn--accent">Message us on WhatsApp</a>
                          <a href={LEAGUES_CONTACT.href} className="lg-call">or call {LEAGUES_CONTACT.label}</a>
                        </>
                      ) : (
                        <a href={BIDCOURT_URL} className={`vv-btn ${p.pick ? "vv-btn--accent" : "vv-btn--ghost"}`}>{p.pick ? "Set up your league" : "Start free"}</a>
                      )}
                    </div>
                  </div>
                  <div className="lg-paddle__handle" aria-hidden="true" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="faq-h" className="lg-band">
          <div className="vv-wrap">
            <h2 id="faq-h" className="vv-display vv-display--italic lg-h2 lg-reveal">Questions organizers ask</h2>
            <div className="lg-faq">
              {FAQ.map((q) => (
                <details key={q.q}>
                  <summary>{q.q}</summary>
                  <p>{q.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="final-h" className="lg-final">
          <div className="vv-wrap">
            <p className="lg-eyebrow lg-reveal">Host it at Vibe &amp; Volley</p>
            <h2 id="final-h" className="vv-display vv-display--italic lg-h2 lg-reveal">Lights on. <span style={{ color: "var(--vv-accent)" }}>Paddles up.</span></h2>
            <p className="lg-sub lg-reveal">Run your auction at our court, then play your league matches here. Call us to plan an auction night.</p>
            <div className="vv-btnrow">
              <Magnetic fine={fine} href={BIDCOURT_URL} className="vv-btn vv-btn--accent vv-btn--lg">Set up your league</Magnetic>
              <Magnetic fine={fine} href={ADMIN_PHONE.href} className="vv-btn vv-btn--ghost vv-btn--lg">Call {ADMIN_PHONE.label}</Magnetic>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter variant="leagues" />
    </div>
  );
}
